import {
  SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
  sortReportRows,
} from "./renderConfig";
import { buildCsv } from "./agentServiceTimeExport";

const SUMMARY_HEADERS = Object.freeze([
  "Scope",
  "Branch",
  "Service",
  "Completed Tickets",
  "Total Service Time",
  "Average",
  "Min",
  "Max",
]);

const DAILY_HEADERS = Object.freeze([
  "Scope",
  "Branch",
  "Service",
  "Day",
  "Completed Tickets",
  "Total Service Time",
  "Average",
]);

const toPrimitiveNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const getPointDate = (point) => point.date ?? point.day ?? point.name ?? point.hour ?? "";
const getPoints = (row) => row.days || row.hours || [];
const pointLabelPrefix = (rows) =>
  rows.some((row) => getPoints(row).some((point) => point?.hour !== undefined && point?.hour !== null))
    ? "Hour"
    : "Day";

export const getServiceTimePerServiceRows = ({ data = [], branchName = "", scope = "" }) =>
  (data || [])
    .filter((service) => toPrimitiveNumber(service?.completed))
    .map((service) => ({
      ...service,
      serviceName: service.name || "",
      branchName,
      scope,
      totalServiceTime: service.sessionInSeconds,
      average: service.avg,
    }));

export const getServiceTimePerServiceRowsForScope = (input, renderConfig) => {
  const config = normalizeRenderConfig(renderConfig, {
    columnDefinitions: SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  });
  return sortReportRows(getServiceTimePerServiceRows(input), config, {
    columnDefinitions: SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  });
};

export const getServiceTimePerServiceSummaryCsvRows = (scopes = [], renderConfig) =>
  scopes.flatMap((scope) =>
    getServiceTimePerServiceRowsForScope(scope, renderConfig).map((row) => [
      row.scope || "",
      row.branchName || "",
      row.serviceName || "",
      toPrimitiveNumber(row.completed),
      toPrimitiveNumber(row.sessionInSeconds),
      toPrimitiveNumber(row.avg),
      toPrimitiveNumber(row.min),
      toPrimitiveNumber(row.max),
    ])
  );

export const getServiceTimePerServiceDailyCsvRows = (scopes = [], renderConfig) =>
  scopes.flatMap((scope) =>
    getServiceTimePerServiceRowsForScope(scope, renderConfig).flatMap((row) =>
      getPoints(row).map((point) => [
        row.scope || "",
        row.branchName || "",
        row.serviceName || "",
        getPointDate(point),
        toPrimitiveNumber(point.completed),
        toPrimitiveNumber(point.sessionInSeconds),
        toPrimitiveNumber(point.avg),
      ])
    )
  );

const getDayLabels = (rows) =>
  Array.from(
    new Set(
      rows.flatMap((row) =>
        getPoints(row).map(getPointDate).filter((date) => date !== undefined && date !== null && date !== "")
      )
    )
  );

export const getServiceTimePerServiceMergedCsvHeaders = (scopes = [], renderConfig) => {
  const rows = scopes.flatMap((scope) => getServiceTimePerServiceRowsForScope(scope, renderConfig));
  const labels = getDayLabels(rows);
  const prefix = pointLabelPrefix(rows);
  return [...SUMMARY_HEADERS, ...labels.map((day) => `${prefix} ${day}`)];
};

export const getServiceTimePerServiceMergedCsvRows = (scopes = [], renderConfig) => {
  const rows = scopes.flatMap((scope) => getServiceTimePerServiceRowsForScope(scope, renderConfig));
  const dayLabels = getDayLabels(rows);
  return rows.map((row) => {
    const valuesByDay = Object.fromEntries(
      getPoints(row).map((point) => [getPointDate(point), toPrimitiveNumber(point.avg)])
    );
    return [
      row.scope || "",
      row.branchName || "",
      row.serviceName || "",
      toPrimitiveNumber(row.completed),
      toPrimitiveNumber(row.sessionInSeconds),
      toPrimitiveNumber(row.avg),
      toPrimitiveNumber(row.min),
      toPrimitiveNumber(row.max),
      ...dayLabels.map((day) => valuesByDay[day] ?? ""),
    ];
  });
};

const triggerCsvDownload = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export const downloadServiceTimePerServiceCsvFiles = ({ scopes, renderConfig, filenamePrefix }) => {
  triggerCsvDownload(
    `${filenamePrefix}.csv`,
    buildCsv(
      getServiceTimePerServiceMergedCsvHeaders(scopes, renderConfig),
      getServiceTimePerServiceMergedCsvRows(scopes, renderConfig)
    )
  );
};
